import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import {
  LayoutDashboard, Users, CreditCard, Settings, FileText, Headphones,
  Building2, LogOut, Menu, X, Receipt, ArrowRightLeft, RotateCcw,
  Bell, ChevronDown, UserCircle2, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AdminLayout = () => {
  const { admin, logout, loading } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Track screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!loading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, loading, navigate]);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('#admin-profile-menu')) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#060d1e' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid rgba(6,182,212,0.2)', borderTopColor: '#06b6d4', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: 'rgba(203,232,248,0.6)', fontSize: 14 }}>Loading admin panel...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const currentUser = admin;

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Get page title from current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin' || path === '/admin/dashboard') return 'Dashboard';
    if (path.includes('/merchants')) return 'Merchants';
    if (path.includes('/kyc')) return 'KYC Requests';
    if (path.includes('/recharges')) return 'Recharge Requests';
    if (path.includes('/direct-recharge')) return 'Wallet Transfer';
    if (path.includes('/qr-settlement')) return 'QR Settlement';
    if (path.includes('/aeps-settlement')) return 'AEPS Operations';
    if (path.includes('/banks')) return 'Bank Master';
    if (path.includes('/tickets')) return 'Support Tickets';
    if (path.includes('/api-configs')) return 'API Configs';
    if (path.includes('/api-requests')) return 'API Requests';
    if (path.includes('/apis')) return 'API Config';
    if (path.includes('/ledger')) return 'Ledger Reports';
    if (path.includes('/services-report')) return 'Services Report';
    if (path.includes('/wallet-adjustments')) return 'Wallet Adjustments';
    if (path.includes('/withdrawals')) return 'Withdrawal Management';
    return 'Admin Panel';
  };

  const navGroups = [
    {
      label: 'Overview',
      items: [
        { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
      ]
    },
    {
      label: 'Merchant Management',
      items: [
        { to: '/admin/merchants', icon: Users, label: 'Merchants' },
        { to: '/admin/kyc', icon: FileText, label: 'KYC Requests' },
        { to: '/admin/recharges', icon: CreditCard, label: 'Recharge Requests' },
        { to: '/admin/direct-recharge', icon: ArrowRightLeft, label: 'Wallet Transfer' },
        { to: '/admin/withdrawals', icon: CreditCard, label: 'Withdrawals' },
      ]
    },
    {
      label: 'Operations',
      items: [
        { to: '/admin/qr-settlement', icon: RotateCcw, label: 'QR Settlement' },
        { to: '/admin/aeps-settlement', icon: RotateCcw, label: 'AEPS Operations' },
        { to: '/admin/banks', icon: Building2, label: 'Bank Master' },
        { to: '/admin/tickets', icon: Headphones, label: 'Support Tickets' },
      ]
    },
    {
      label: 'Configuration',
      items: [
        { to: '/admin/api-configs', icon: Settings, label: 'API Configs' },
        { to: '/admin/api-requests', icon: Settings, label: 'API Requests' },
        { to: '/admin/apis', icon: Settings, label: 'API Config' },
      ]
    },
    {
      label: 'Reports',
      items: [
        { to: '/admin/ledger', icon: Receipt, label: 'Ledger Reports' },
        { to: '/admin/services-report', icon: FileText, label: 'Services Report' },
        { to: '/admin/wallet-adjustments', icon: FileText, label: 'Wallet Adjustments' },
      ]
    },
  ];

  const sidebarStyle = {
    background: 'linear-gradient(180deg, #060d1e 0%, #071222 60%, #060d1e 100%)',
    borderRight: '1px solid rgba(6,182,212,0.15)',
  };

  const SidebarContent = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', ...sidebarStyle }}>
      {/* Logo */}
      <div style={{ padding: '18px 18px 14px', borderBottom: '1px solid rgba(6,182,212,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <img
          src="/payntric_logo.png"
          alt="PayNTric"
          style={{ height: 34, width: 'auto', filter: 'drop-shadow(0 0 10px rgba(6,182,212,0.5))' }}
        />
      </div>

      {/* Nav only — user info is in topbar */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 10px', scrollbarWidth: 'thin', scrollbarColor: 'rgba(6,182,212,0.2) transparent' }}>
        {navGroups.map((group) => (
          <div key={group.label} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'rgba(125,211,252,0.35)', padding: '8px 10px 4px' }}>
              {group.label}
            </div>
            {group.items.map(({ to, icon: Icon, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setSidebarOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '7px 10px',
                  borderRadius: 8,
                  marginBottom: 1,
                  textDecoration: 'none',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#67e8f9' : 'rgba(148,163,184,0.85)',
                  background: isActive ? 'rgba(6,182,212,0.10)' : 'transparent',
                  borderLeft: isActive ? '2px solid #06b6d4' : '2px solid transparent',
                  transition: 'all 0.15s ease',
                })}
                className="admin-nav-link"
              >
                <Icon size={17} style={{ flexShrink: 0 }} />
                {label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <style>{`
        .admin-nav-link:hover {
          color: rgba(103,232,249,0.9) !important;
          background: rgba(6,182,212,0.06) !important;
        }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 39, backdropFilter: 'blur(2px)' }}
        />
      )}

      {/* Sidebar */}
      <aside
        style={{
          position: 'fixed', top: 0, left: 0, bottom: 0, width: 220, zIndex: 40,
          transform: isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
          transition: 'transform 0.25s ease',
        }}
      >
        <SidebarContent />
      </aside>

      {/* Top Bar */}
      <header style={{
        position: 'fixed', top: 0, right: 0, left: isMobile ? 0 : 220, height: 58, zIndex: 30,
        background: '#ffffff', borderBottom: '1px solid #e5e7eb',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}>
        {/* Left: Mobile toggle + page title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{ display: isMobile ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center', padding: 6, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b' }}
          >
            <Menu size={20} />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>{getPageTitle()}</h1>
            <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>Admin Panel</p>
          </div>
        </div>

        {/* Right: Profile */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Profile dropdown */}
          <div id="admin-profile-menu" style={{ position: 'relative' }}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                borderRadius: 8, border: '1px solid #e2e8f0', background: profileOpen ? '#f8fafc' : '#ffffff',
                cursor: 'pointer', transition: 'all 0.15s'
              }}
            >
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0
              }}>
                {currentUser?.username?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', lineHeight: 1 }}>{currentUser?.username || 'Admin'}</div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>Super Admin</div>
              </div>
              <ChevronDown size={14} style={{ color: '#94a3b8', transform: profileOpen ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} />
            </button>

            {/* Dropdown */}
            {profileOpen && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 6,
                background: '#ffffff', borderRadius: 10, border: '1px solid #e2e8f0',
                boxShadow: '0 10px 30px rgba(0,0,0,0.12)', minWidth: 200, zIndex: 50,
                animation: 'fadeIn 0.15s ease'
              }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', background: 'linear-gradient(135deg, #f8fafc, #f0fdfe)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 15, fontWeight: 700, color: '#fff'
                    }}>
                      {currentUser?.username?.charAt(0)?.toUpperCase() || 'A'}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{currentUser?.username || 'Admin'}</div>
                      <div style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                        <Shield size={10} style={{ color: '#06b6d4' }} /> Super Administrator
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ padding: 6 }}>
                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                      borderRadius: 7, border: 'none', background: 'transparent', cursor: 'pointer',
                      fontSize: 13, color: '#ef4444', fontWeight: 500,
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main style={{ marginLeft: isMobile ? 0 : 220, paddingTop: 58, minHeight: '100vh' }}>
        <div style={{ padding: isMobile ? 12 : 24 }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
